// ============================================================================
// ROOM STATUS MODAL (E2 Front Office Workspace — Room Board)
// ============================================================================
// Manual housekeeping status update for one physical room. Only the manual
// codes are offered — occupancy and OOO are derived states owned by check-in
// and the OOO register. Lifecycle-modal pattern (no dirty tracking).

import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { frontOfficeService } from 'api/services/frontOfficeService';
import type { RoomBoardRoom } from 'api/types/frontOffice.types';
import notificationService from 'services/notificationService';

interface RoomStatusModalProps {
  isOpen: boolean;
  room: RoomBoardRoom | null;
  onClose: () => void;
  onUpdated: () => void;
}

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ROOM_AVAILABLE', label: 'Available (clean, ready to sell)' },
  { value: 'ROOM_DIRTY', label: 'Dirty (needs housekeeping)' },
  { value: 'ROOM_INSPECTED', label: 'Inspected (housekeeping verified)' },
];

const RoomStatusModal: React.FC<RoomStatusModalProps> = ({
  isOpen, room, onClose, onUpdated,
}) => {
  const [statusCode, setStatusCode] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && room) {
      setStatusCode(
        STATUS_OPTIONS.some((o) => o.value === room.housekeepingStatusCode)
          ? room.housekeepingStatusCode!
          : 'ROOM_DIRTY',
      );
      setReason('');
    }
  }, [isOpen, room]);

  if (!isOpen || !room) return null;

  const handleConfirm = async () => {
    if (!statusCode) return;
    setSubmitting(true);
    try {
      await frontOfficeService.updateRoomStatus(room.roomId, {
        statusCode,
        reason: reason.trim() || undefined,
      });
      notificationService.toast.success(`Room ${room.roomNumber} status updated.`);
      onUpdated();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.errors?.[0]
        || err?.message
        || 'Failed to update room status.';
      notificationService.toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={submitting ? undefined : onClose} />
      <div className="modal-container modal-sm">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Room {room.roomNumber} — Housekeeping Status</h2>
          <button className="modal-close" onClick={onClose} disabled={submitting}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{
            padding: '0.75rem 0.875rem', background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)', borderRadius: '6px',
            marginBottom: '0.875rem', fontSize: '0.85rem',
          }}>
            <div><strong>Room:</strong> {room.roomNumber} · {room.categoryName || room.categoryCode}</div>
            <div><strong>Current state:</strong> {room.derivedState}</div>
            {room.isOccupied && (
              <div style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', fontSize: '0.8rem' }}>
                Occupied by {room.occupantGuestName || room.occupantBookingNumber}. The
                housekeeping status is recorded but the board keeps showing Occupied
                until check-out.
              </div>
            )}
          </div>

          <div className="form-group">
            <SyutSelect
              label="New housekeeping status"
              required
              options={STATUS_OPTIONS}
              value={statusCode}
              onChange={(val) => setStatusCode((val as string) || '')}
              size="sm"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Remarks</label>
            <input
              type="text"
              className="form-input"
              value={reason}
              maxLength={200}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Optional note for the status log"
              disabled={submitting}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleConfirm} disabled={submitting || !statusCode}>
            {submitting ? 'Updating…' : 'Update Status'}
          </button>
        </div>
      </div>
    </>
  );
};

export default RoomStatusModal;
